import { useCart } from "../context/CartContext";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { useAuth } from "../context/AuthContext";

export default function Checkout() {
  const { cart } = useCart();
  const { user } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  // { flat_fee, free_over } from the delivery_settings table; null until loaded
  const [delivery, setDelivery] = useState(null);
  const [deliveryError, setDeliveryError] = useState(false);

  // Display only. The server recalculates the real total from your products table.
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const freeOver = delivery?.free_over ?? null;
  const qualifiesForFree =
    freeOver !== null && Math.round(subtotal * 100) >= Math.round(freeOver * 100);
  const deliveryFee = !delivery || qualifiesForFree ? 0 : delivery.flat_fee;
  const total = subtotal + deliveryFee;
  const awayFromFree = delivery && freeOver !== null && !qualifiesForFree ? freeOver - subtotal : null;
  const itemCount = cart.reduce((sum, item) => sum + item.qty, 0);

  // Delivery prices: the same row the server uses when it creates the payment
  useEffect(() => {
    let cancelled = false;

    async function loadDelivery() {
      const { data, error } = await supabase
        .from("delivery_settings")
        .select("flat_fee, free_over")
        .eq("id", 1)
        .maybeSingle();

      if (cancelled) return;
      if (error || !data) {
        console.error("Could not load delivery settings:", error);
        setDeliveryError(true);
        return;
      }
      setDelivery({
        flat_fee: Number(data.flat_fee),
        free_over: data.free_over == null ? null : Number(data.free_over),
      });
    }

    loadDelivery();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefill from auth + profile
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    setEmail(user.email || "");

    async function loadProfile() {
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name, address, city, postcode")
        .eq("id", user.id)
        .maybeSingle();

      if (error) {
        console.warn("Could not load profile:", error.message);
        return;
      }
      if (data && !cancelled) {
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setAddress(data.address || "");
        setCity(data.city || "");
        setPostcode(data.postcode || "");
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // If the customer hits Back from Stripe, the browser may restore this page
  // from its back/forward cache with loading still true.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setLoading(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  async function getErrorMessage(error) {
    const fallback = "We couldn't start your payment. Please try again.";
    try {
      const body = await error?.context?.json?.();
      return body?.error || fallback;
    } catch {
      return fallback;
    }
  }

  async function handleCheckout(e) {
    e.preventDefault();
    if (loading) return;
    setMessage("");

    if (cart.length === 0) {
      setMessage("Your cart is empty.");
      return;
    }
    if (!email.trim()) {
      setMessage("Please enter your email.");
      return;
    }
    if (![firstName, lastName, address, city, postcode].every((v) => v.trim())) {
      setMessage("Please complete your postage details.");
      return;
    }

    setLoading(true);

    try {
      // Save postage details to the profile (best effort, never blocks payment)
      if (user) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            address: address.trim(),
            city: city.trim(),
            postcode: postcode.trim(),
          })
          .eq("id", user.id);

        if (profileError) console.warn("Profile update failed:", profileError.message);
      }

      // The server creates the order and prices it. We only send ids and quantities.
      // functions.invoke attaches the user's session token (or the anon key for guests).
      const { data, error } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: {
            email: email.trim(),
            items: cart.map(({ id, qty }) => ({ id, qty })),
            shipping: {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              address: address.trim(),
              city: city.trim(),
              postcode: postcode.trim(),
            },
          },
        }
      );

      if (error) {
        console.error("create-checkout-session error:", error);
        setMessage(await getErrorMessage(error));
        setLoading(false);
        return;
      }
      if (!data?.url) throw new Error("No checkout URL returned");

      // Leave loading = true while the browser navigates to Stripe
      window.location.assign(data.url);
    } catch (err) {
      console.error("Checkout failed:", err);
      setMessage("Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  if (cart.length === 0) {
    return (
      <div className="co">
        <h2 className="co-title">Checkout</h2>
        <div className="shop-empty">
          <h3>Your cart is empty</h3>
          <p>Add something to your cart before checking out.</p>
          <Link to="/products" className="ui-btn ui-btn--primary">
            Browse products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="co">
      <h2 className="co-title">Checkout</h2>

      <form onSubmit={handleCheckout}>
        <div className="co-layout">
          {/* DETAILS */}
          <div className="co-card">
            <section className="co-section">
              <h3>Contact</h3>
              <div className="ui-field">
                <label className="ui-label" htmlFor="co-email">Email address</label>
                <input
                  id="co-email"
                  className="ui-input"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  disabled={loading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <p className="ui-hint">We'll send your order confirmation here.</p>
              </div>
            </section>

            <section className="co-section">
              <h3>Delivery address</h3>

              <div className="co-grid-2">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="co-first">First name</label>
                  <input
                    id="co-first"
                    className="ui-input"
                    type="text"
                    autoComplete="given-name"
                    required
                    disabled={loading}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="ui-field">
                  <label className="ui-label" htmlFor="co-last">Last name</label>
                  <input
                    id="co-last"
                    className="ui-input"
                    type="text"
                    autoComplete="family-name"
                    required
                    disabled={loading}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="ui-field">
                <label className="ui-label" htmlFor="co-address">House number and street name</label>
                <input
                  id="co-address"
                  className="ui-input"
                  type="text"
                  autoComplete="address-line1"
                  required
                  disabled={loading}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              <div className="co-grid-2">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="co-city">City</label>
                  <input
                    id="co-city"
                    className="ui-input"
                    type="text"
                    autoComplete="address-level2"
                    required
                    disabled={loading}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className="ui-field">
                  <label className="ui-label" htmlFor="co-postcode">Postcode</label>
                  <input
                    id="co-postcode"
                    className="ui-input"
                    type="text"
                    autoComplete="postal-code"
                    required
                    disabled={loading}
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                  />
                </div>
              </div>
            </section>
          </div>

          {/* ORDER SUMMARY */}
          <aside className="co-card co-summary" aria-label="Order summary">
            <div className="co-summary-head">
              <h3>Order summary</h3>
              <Link to="/cart" className="co-edit">Edit cart</Link>
            </div>

            <ul className="co-items">
              {cart.map((item) => (
                <li key={item.id} className="co-item">
                  <div className="co-thumb-wrap">
                    {item.image_url ? (
                      <img className="co-thumb" src={item.image_url} alt="" />
                    ) : (
                      <span className="co-thumb co-thumb--empty" aria-hidden="true">
                        {item.name?.charAt(0)}
                      </span>
                    )}
                    <span className="co-qty-badge" aria-hidden="true">{item.qty}</span>
                  </div>

                  <div>
                    <div className="co-item-name">{item.name}</div>
                    <div className="co-item-meta">
                      Qty {item.qty} · £{Number(item.price).toFixed(2)} each
                    </div>
                  </div>

                  <div className="co-item-price">
                    £{(item.price * item.qty).toFixed(2)}
                  </div>
                </li>
              ))}
            </ul>

            <div className="co-row">
              <span>Items ({itemCount})</span>
              <span>£{subtotal.toFixed(2)}</span>
            </div>
            <div className="co-row">
              <span>Delivery</span>
              <span>{delivery ? (deliveryFee === 0 ? "Free" : `£${deliveryFee.toFixed(2)}`) : "..."}</span>
            </div>
            {awayFromFree !== null && (
              <p className="co-free-hint">
                Spend £{awayFromFree.toFixed(2)} more to get free delivery.
              </p>
            )}
            <div className="co-row co-total">
              <span>Total</span>
              <span>£{total.toFixed(2)}</span>
            </div>

            {deliveryError && (
              <p className="ui-alert ui-alert--error" role="alert" style={{ marginTop: "1rem" }}>
                We couldn't load the delivery price. Please refresh the page and try again.
              </p>
            )}

            {message && (
              <p className="ui-alert ui-alert--error" role="alert" style={{ marginTop: "1rem" }}>
                {message}
              </p>
            )}

            <button
              type="submit"
              className="ui-btn ui-btn--primary ui-btn--block co-pay"
              disabled={loading || cart.length === 0 || !delivery}
            >
              {loading
                ? "Redirecting to payment..."
                : delivery
                  ? `Pay £${total.toFixed(2)}`
                  : "Loading prices..."}
            </button>

            <p className="co-secure">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Payments are processed securely by Stripe. We never see your card details.
            </p>
          </aside>
        </div>
      </form>
    </div>
  );
}